@file:Suppress("DEPRECATION")
package org.example.newportfolio.components.sections.about

import androidx.compose.runtime.Composable
import org.example.newportfolio.components.widgets.section.SectionContainer
import org.example.newportfolio.components.widgets.section.SectionContainerStyle
import org.example.newportfolio.components.widgets.skill.SkillItem
import org.example.newportfolio.components.sections.about.components.AboutSectionSkillsGridVariant
import com.varabyte.kobweb.compose.css.CSSPosition
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.RadialGradient
import com.varabyte.kobweb.compose.css.functions.radialGradient
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.style.addVariant
import com.varabyte.kobweb.silk.components.style.toModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.example.newportfolio.models.Section
import org.example.newportfolio.models.Skill
import org.example.newportfolio.theme.brand
import org.example.newportfolio.theme.fonts.*
import org.example.newportfolio.utils.Res
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh

val AboutSectionVariant by SectionContainerStyle.addVariant {
    val colorPalette = colorMode.toPalette()
    base {
        // Top/bottom padding + restore responsive side padding (silk's CSS-in-JS
        // collapses padding-top/padding-bottom into the `padding` shorthand,
        // which would otherwise reset left/right to 0).
        Modifier.styleModifier {
            property("padding-top", "4rem")
            property("padding-bottom", "4rem")
            property("padding-left", "16px")
            property("padding-right", "16px")
        }
    }
    Breakpoint.ZERO {
        Modifier
            .maxWidth(100.percent)
            .backgroundImage(
                radialGradient(RadialGradient.Shape.Circle, CSSPosition(x = 50.percent, y = 30.percent)) {
                    add(colorPalette.brand.primary.toRgb().copyf(alpha = if (colorMode.isDark) 0.1f else 0.2f))
                    add(Colors.Transparent, 50.percent)
                }
            )
    }
    Breakpoint.SM {
        Modifier.maxWidth(100.percent).styleModifier {
            property("padding-left", "28px")
            property("padding-right", "28px")
        }
    }
    Breakpoint.MD {
        Modifier
            .maxWidth(90.percent)
            .minHeight(100.vh)
            .styleModifier {
                property("padding-left", "48px")
                property("padding-right", "48px")
            }
            .backgroundImage(
                radialGradient(RadialGradient.Shape.Circle, CSSPosition(x = 50.percent, y = 50.percent)) {
                    add(colorPalette.brand.primary.toRgb().copyf(alpha = if (colorMode.isDark) 0.1f else 0.2f))
                    add(Colors.Transparent, 63.percent)
                }
            )
    }
    Breakpoint.LG {
        Modifier.maxWidth(80.percent).styleModifier {
            property("padding-left", "94px")
            property("padding-right", "94px")
        }
    }
    Breakpoint.XL {
        Modifier.maxWidth(70.percent).styleModifier {
            property("padding-left", "134px")
            property("padding-right", "134px")
        }
    }
}

@Composable
fun AboutSection() {
    SectionContainer(
        modifier = Modifier,
        variant = AboutSectionVariant,
        section = Section.About,
        verticalArrangement = Arrangement.Top,
    ) {
        TextContent()
        SimpleGrid(
            variant = AboutSectionSkillsGridVariant,
            numColumns = numColumns(base = 2, md = 5, lg = 5)
        ) {
            // Skill chips cascade in as the grid scrolls into view.
            // Modulo 8 — animations.css only ships stagger-0..7; beyond
            // that the wait reads as a stall rather than choreography.
            Skill.entries.forEachIndexed { index, skill ->
                SkillItem(
                    skill = skill,
                    modifier = Modifier
                        .flexGrow(1)
                        .classNames("scroll-reveal", "stagger-${index % 8}")
                )
            }
        }
    }
}

@Composable
private fun TextContent() {
    Column(
        modifier = Modifier
            .padding(top = 8.5.cssRem)
            .fillMaxWidth()
            .classNames("scroll-reveal"),  // heading + subtitle fade up on scroll-in
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        SpanText(
            modifier = TextStyle.toModifier(DisplayTextStyle, TextStylePrimaryColor)
                .textAlign(TextAlign.Center),
            text = Res.Strings.ABOUT_SECTION_TITLE
        )
        SpanText(
            modifier = TextStyle.toModifier(SubDisplayTextStyle, TextStyleSecondaryColor)
                .padding(top = 1.cssRem)
                .textAlign(TextAlign.Center),
            text = Res.Strings.ABOUT_SECTION_SUBTITLE
        )
    }
}